/**
 * Utility functions for Firestore API
 */

/**
 * Calculates distance based on RSSI value
 * @param {number} rssi - Received Signal Strength Indicator value
 * @returns {number} Distance in meters
 */
export function calculateDistance(rssi) {
  return 10 * ((-70 - rssi) / 20);
}

/**
 * Categorizes Firestore errors by type
 * @param {Error} error - The error object
 * @returns {string} Error category
 */
function categorizeError(error) {
  const code = error.code || "";

  if (code.includes("permission-denied") || code.includes("unauthenticated")) {
    return "PERMISSION";
  }
  if (code.includes("not-found")) {
    return "NOT_FOUND";
  }
  if (code.includes("already-exists")) {
    return "DUPLICATE";
  }
  if (code.includes("resource-exhausted") || code.includes("unavailable")) {
    return "RATE_LIMIT";
  }
  if (code.includes("cancelled") || code.includes("deadline-exceeded")) {
    return "TIMEOUT";
  }
  if (code.includes("invalid-argument") || code.includes("failed-precondition")) {
    return "VALIDATION";
  }
  return "UNKNOWN";
}

/**
 * Helper function to handle errors uniformly across the API
 * @param {Error} error - The error object
 * @param {string} operation - The operation that failed
 * @throws {Error} Rethrows the error with additional context
 */
export function handleError(error, operation) {
  console.error(`Error in ${operation}:`, error);
  throw error;
}

/**
 * Common error handler for Firestore operations
 * Provides consistent error logging and categorization
 * @param {Error} error - The error object to handle
 * @param {string} [serviceName] - Name of the service (e.g., "animalService")
 * @param {string} [functionName] - Name of the function (e.g., "getAnimals")
 * @param {Object} [context] - Additional context for debugging
 * @throws {Error} Rethrows the error
 */
export function handleFirestoreError(error, serviceName, functionName, context) {
  const category = categorizeError(error);
  const prefix = serviceName && functionName
    ? `[${serviceName}][${functionName}]`
    : serviceName
      ? `[${serviceName}]`
      : "[Firestore]";

  const message = `${prefix} - ${category} Error: ${error.message || error}`;

  if (context) {
    console.error(message, { context, code: error.code });
  } else {
    console.error(message, { code: error.code });
  }

  throw error;
} 